// Die Rechnung hinter der Karte — **ohne Phaser** (Gate FG-18).
//
// Eine Karte ist zwei Dinge: eine Abbildung (Weltkoordinate -> Bildpunkt) und
// eine Liste von Marken. Beides ist reine Rechnung und gehoert deshalb hierher
// und nicht in die Szene; dort steht nur, wie es aussieht.
//
// Der Grund ist derselbe wie bei `gfx/zonecolor.ts`: was im Zeichenpfad
// gerechnet wird, ist ohne Browser nicht pruefbar — und eine Karte, die den
// falschen Punkt anzeigt, meldet keinen Fehler. Sie zeigt einfach woanders hin.

import { balancing, planets, sideQuest, zonesFor } from '../data/balancing';
import type { DerivedStats, SideQuest } from '../data/types';
import { mapRadius } from './navigation';
import { planetAt } from './planets';
import { openStep, stepPoint, type Point, type SideSave } from './sidequests';

/** Was auf der Karte steht — jede Marke kennt ihren Ort und ihre Sorte. */
export interface MapMark {
  kind: 'base' | 'ship' | 'side' | 'course' | 'planet';
  x: number;
  y: number;
  label: string;
  /** Die ID des Nebenauftrags oder Planeten, wenn die Marke einer ist — sonst leer. */
  id: string;
}

/**
 * Die Ringe der Zonen, von innen nach aussen.
 *
 * Schreibt in ein uebergebenes Zielarray (Gate FG-14): die Szene ruft es bei
 * jeder Aenderung, nicht bei jedem Bild — aber die Regel gilt trotzdem, und
 * eine Ausnahme „nur hier" ist der Anfang der naechsten.
 */
export function zoneRings(era: number, out: number[]): number[] {
  out.length = 0;
  for (const zone of zonesFor(era)) if (zone.from > 0) out.push(zone.from);
  return out;
}

/**
 * Alle Marken der Karte — Kapsel, Schiff, offene Auftragsschritte, Kurs.
 *
 * **Nur der jeweils naechste Schritt** eines Auftrags steht darauf. Alle
 * Schritte zugleich zu zeigen waere ein Feld voller Punkte, von denen die
 * meisten nichts ausloesen — und der Spieler suchte sich den richtigen heraus,
 * statt ihn zu sehen.
 */
export function mapMarks(
  save: SideSave,
  built: ReadonlySet<string>,
  seed: number,
  ship: Point,
  course: Point | null,
  out: MapMark[],
): MapMark[] {
  out.length = 0;
  const [bx, by] = balancing().world.dockPoint;
  out.push({ kind: 'base', x: bx, y: by, label: 'Kapsel', id: '' });

  const punkt: Point = { x: 0, y: 0 };
  for (const quest of balancing().sideQuests) {
    const step = openStep(save, quest, built);
    if (step === null) continue;
    stepPoint(quest, step, seed, save.era, punkt);
    out.push({
      kind: 'side',
      x: punkt.x,
      y: punkt.y,
      label: `${quest.name} — ${quest.steps[step].label}`,
      id: quest.id,
    });
  }

  // Die Planeten des Heimatsystems (Akt III). Anders als ein Fund stehen sie
  // auf der Karte: sie sind Orte, keine Suche — und im Feld ohnehin nicht zu
  // übersehen, sobald man in der Nähe ist.
  if (save.era >= balancing().colony.minEra) {
    for (const planet of planets()) {
      planetAt(planet.id, seed, save.era, punkt);
      out.push({ kind: 'planet', x: punkt.x, y: punkt.y, label: planet.name, id: planet.id });
    }
  }

  if (course) out.push({ kind: 'course', x: course.x, y: course.y, label: 'Kurs', id: '' });
  out.push({ kind: 'ship', x: ship.x, y: ship.y, label: 'Du', id: '' });
  return out;
}

/**
 * Die naechste Marke zu einem Punkt — fuer den Klick auf die Karte.
 *
 * `maxDist` ist in **Weltkoordinaten** gemeint: die Szene rechnet den
 * Bildschirmabstand vorher um, damit dieselbe Toleranz bei jedem Zoom gilt.
 * Kapsel und Schiff kommen nicht in Frage — ein Kurs auf sich selbst waere
 * kein Kurs.
 */
export function markAt(marks: readonly MapMark[], x: number, y: number, maxDist: number): MapMark | null {
  let best: MapMark | null = null;
  let bestSq = maxDist * maxDist;
  for (const mark of marks) {
    if (mark.kind === 'ship') continue;
    const dSq = (mark.x - x) ** 2 + (mark.y - y) ** 2;
    if (dSq <= bestSq) { bestSq = dSq; best = mark; }
  }
  return best;
}

/**
 * Der Massstab: wie viele Weltpunkte auf einen Bildpunkt gehen.
 *
 * Die Karte zeigt einen Kreis um die Kapsel mit dem Halbmesser `mapRange` —
 * ein Schiffswert, der ueber den Kartografie-Zweig waechst. Wer weiter sehen
 * will, kauft es; das ist derselbe Handel wie bei der Sensorik.
 */
export function mapScale(stats: DerivedStats, width: number, height: number): number {
  const radius = mapRadius(stats);
  return Math.min(width, height) / (radius * 2);
}

/** Der Auftrag zu einer Marke — oder `undefined`, wenn sie keiner ist. */
export function markQuest(mark: MapMark | null): SideQuest | undefined {
  return mark && mark.kind === 'side' ? sideQuest(mark.id) : undefined;
}
